import React from "react";
import SidbarComponent from "./SidbarComponent";
import TopBarComponent from "./TopBarComponent";
import { FooterComponents } from "./FooterComponent";
import { getUserInfo, getUserRole } from "../utils/auth";
import { formatRole } from "./navigation";
import {
  GraduationCap,
  Globe,
  Users,
  Award,
  BookOpen,
  Calendar,
  CheckCircle,
  Star,
  Target,
  Building2,
  UserCheck,
} from "lucide-react";

/* ------------------------------------------------------------------ */
/* Content (unchanged copy, restyled)                                  */
/* ------------------------------------------------------------------ */
const PILLARS = [
  {
    icon: GraduationCap,
    tone: "cyan",
    title: "Academic Excellence",
    text: "Fostering world-class education standards and promoting academic excellence across all disciplines.",
  },
  {
    icon: Users,
    tone: "green",
    title: "Student Success",
    text: "Empowering students to achieve their educational dreams and contribute to Rwanda's development.",
  },
  {
    icon: Globe,
    tone: "purple",
    title: "Global Partnerships",
    text: "Building international collaborations to expand educational opportunities worldwide.",
  },
  {
    icon: Award,
    tone: "orange",
    title: "Merit Recognition",
    text: "Recognizing and rewarding outstanding academic performance and leadership potential.",
  },
];

const VALUES = [
  {
    icon: Target,
    tone: "cyan",
    title: "Quality Education",
    text:
      "We are committed to providing access to quality higher education that meets international standards. Our programs are designed to nurture critical thinking, innovation, and leadership skills essential for Rwanda's transformation into a knowledge-based economy.",
  },
  {
    icon: CheckCircle,
    tone: "green",
    title: "Inclusive Access",
    text:
      "Education is a fundamental right. We ensure equitable access to higher education opportunities for all Rwandan students, regardless of their socio-economic background, promoting diversity and inclusion in our academic institutions.",
  },
  {
    icon: Globe,
    tone: "purple",
    title: "Global Competence",
    text:
      "We prepare students to compete globally while remaining rooted in Rwandan values. Through international partnerships and exchange programs, we expose students to global perspectives and best practices in their fields of study.",
  },
  {
    icon: Star,
    tone: "orange",
    title: "Excellence Culture",
    text:
      "We foster a culture of excellence that encourages students to strive for the highest academic and professional standards. Our commitment to excellence drives continuous improvement in all our educational programs and services.",
  },
];

const COMMITMENTS = [
  { icon: Award, tone: "cyan", title: "Excellence", text: "Committed to the highest standards of academic and professional excellence" },
  { icon: Users, tone: "green", title: "Inclusion", text: "Ensuring equal opportunities for all students regardless of background" },
  { icon: Globe, tone: "purple", title: "Innovation", text: "Embracing cutting-edge approaches to education and research" },
];

const TONES = {
  cyan: { icon: "bg-cyan-50 text-cyan-700 ring-cyan-100", bar: "border-l-cyan-500" },
  green: { icon: "bg-emerald-50 text-emerald-700 ring-emerald-100", bar: "border-l-emerald-500" },
  purple: { icon: "bg-violet-50 text-violet-700 ring-violet-100", bar: "border-l-violet-500" },
  orange: { icon: "bg-orange-50 text-orange-700 ring-orange-100", bar: "border-l-orange-500" },
};

function IconBadge({ icon: Icon, tone, size = "md" }) {
  const dims = size === "lg" ? "w-12 h-12" : "w-10 h-10";
  const iconDims = size === "lg" ? "w-6 h-6" : "w-5 h-5";
  return (
    <span className={`inline-flex ${dims} items-center justify-center rounded-xl ring-1 ${TONES[tone].icon}`}>
      <Icon className={iconDims} />
    </span>
  );
}

function SectionTitle({ icon: Icon, children }) {
  return (
    <h2 className="text-lg sm:text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
      <Icon className="w-5 h-5 text-cyan-600" />
      {children}
    </h2>
  );
}

export default function Dashboard() {
  const info = getUserInfo();
  const userName = info?.firstName || "User";
  const userRole = getUserRole() || "Student";
  const today = new Date().toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <TopBarComponent />
      <aside
        id="logo-sidebar"
        className="app-legacy-aside transition-transform -translate-x-full sm:translate-x-0 bg-white border-r border-slate-200"
        aria-label="Sidebar"
      >
        <SidbarComponent />
      </aside>

      <div className="sm:ml-64 flex-1 flex flex-col">
        <main className="flex-1 p-4 sm:p-6 lg:p-8 space-y-8">
          {/* Welcome */}
          <section className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-cyan-900 via-cyan-800 to-cyan-700 text-white shadow-lg">
            <div className="pointer-events-none absolute -right-16 -top-16 w-64 h-64 rounded-full bg-white/5" />
            <div className="pointer-events-none absolute right-24 -bottom-24 w-56 h-56 rounded-full bg-white/5" />
            <div className="relative p-6 sm:p-8 lg:p-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-cyan-200">{today}</p>
                <h1 className="mt-2 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">
                  Welcome back, <span className="text-cyan-200">{userName}</span>
                </h1>
                <p className="mt-2 text-sm sm:text-base text-cyan-100 max-w-2xl">
                  Higher Education Council - Rwanda's Gateway to International Excellence
                </p>
                <div className="mt-5 flex flex-wrap gap-2">
                  <span className="inline-flex items-center gap-2 rounded-lg bg-white/10 ring-1 ring-white/15 px-3 py-1.5 text-sm">
                    <UserCheck className="w-4 h-4" />
                    {formatRole(userRole)}
                  </span>
                  <span className="inline-flex items-center gap-2 rounded-lg bg-white/10 ring-1 ring-white/15 px-3 py-1.5 text-sm">
                    <Calendar className="w-4 h-4" />
                    Academic Year 2024-25
                  </span>
                </div>
              </div>
              <div className="hidden lg:flex w-28 h-28 items-center justify-center rounded-full bg-white/10 ring-1 ring-white/15">
                <Building2 className="w-14 h-14 text-white" />
              </div>
            </div>
          </section>

          {/* Pillars */}
          <section>
            <SectionTitle icon={Star}>Excellence in Higher Education</SectionTitle>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              {PILLARS.map((p) => (
                <article key={p.title} className="app-card p-5 hover:shadow-md transition-shadow">
                  <IconBadge icon={p.icon} tone={p.tone} />
                  <h3 className="mt-4 text-base font-semibold text-slate-900">{p.title}</h3>
                  <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{p.text}</p>
                </article>
              ))}
            </div>
          </section>

          {/* Values */}
          <section>
            <SectionTitle icon={BookOpen}>Our Educational Values</SectionTitle>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {VALUES.map((v) => (
                <article key={v.title} className={`app-card border-l-4 ${TONES[v.tone].bar} p-5 sm:p-6 hover:shadow-md transition-shadow`}>
                  <div className="flex items-start gap-4">
                    <IconBadge icon={v.icon} tone={v.tone} />
                    <div>
                      <h3 className="text-base sm:text-lg font-semibold text-slate-900">{v.title}</h3>
                      <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{v.text}</p>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </section>

          {/* Vision & Mission */}
          <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <article className="app-card p-6 sm:p-8 bg-gradient-to-br from-cyan-50 to-white border-l-4 border-l-cyan-500">
              <IconBadge icon={Target} tone="cyan" size="lg" />
              <h2 className="mt-4 text-xl font-bold text-slate-900">Our Vision</h2>
              <p className="mt-2 text-sm sm:text-base text-slate-600 leading-relaxed">
                To be a leading institution in promoting and regulating quality higher education in Rwanda, fostering innovation,
                research excellence, and producing globally competitive graduates who contribute to Rwanda's socio-economic
                transformation and sustainable development.
              </p>
            </article>
            <article className="app-card p-6 sm:p-8 bg-gradient-to-br from-emerald-50 to-white border-l-4 border-l-emerald-500">
              <IconBadge icon={CheckCircle} tone="green" size="lg" />
              <h2 className="mt-4 text-xl font-bold text-slate-900">Our Mission</h2>
              <p className="mt-2 text-sm sm:text-base text-slate-600 leading-relaxed">
                To regulate, coordinate, and promote quality higher education in Rwanda through effective policies, strategic
                partnerships, and innovative scholarship programs that ensure equitable access to education and develop human
                capital for national development.
              </p>
            </article>
          </section>

          {/* Commitments */}
          <section className="app-card p-6 sm:p-8">
            <div className="text-center max-w-3xl mx-auto">
              <span className="inline-flex w-14 h-14 items-center justify-center rounded-full bg-cyan-700 text-white shadow-md">
                <Building2 className="w-7 h-7" />
              </span>
              <h2 className="mt-4 text-2xl font-bold text-slate-900">Higher Education Council Rwanda</h2>
              <p className="mt-3 text-sm sm:text-base text-slate-600 leading-relaxed">
                Transforming lives through education, building tomorrow's leaders today. We are dedicated to creating pathways to
                excellence, fostering innovation, and ensuring that every student has the opportunity to reach their full potential
                in contributing to Rwanda's bright future.
              </p>
            </div>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
              {COMMITMENTS.map((c) => (
                <div key={c.title} className="rounded-xl border border-slate-200 bg-slate-50/60 p-5 text-center">
                  <div className="flex justify-center">
                    <IconBadge icon={c.icon} tone={c.tone} />
                  </div>
                  <h3 className="mt-3 text-base font-semibold text-slate-900">{c.title}</h3>
                  <p className="mt-1 text-sm text-slate-600">{c.text}</p>
                </div>
              ))}
            </div>
          </section>
        </main>

        <FooterComponents />
      </div>
    </div>
  );
}
